import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Autocomplete,
  Box,
  Typography,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import {
  Close as CloseIcon,
  AttachFile as AttachFileIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { supportTicketApi } from 'shared/services/supportTicketApi';
import { useAuth } from 'shared/contexts/AuthContext';
import { userApi } from 'shared/services/userApi';

interface UserOption {
  _id: string;
  name: string;
  email: string;
}

const CATEGORY_OPTIONS = [
  { value: 'problema_plataforma', label: 'Problema de Plataforma' },
  { value: 'error_sistema', label: 'Error del Sistema' },
  { value: 'solicitud_funcionalidad', label: 'Solicitud de Funcionalidad' },
  { value: 'consulta', label: 'Consulta' },
  { value: 'otro', label: 'Otro' },
];

const SEVERITY_OPTIONS = [
  { value: 'leve', label: 'Leve' },
  { value: 'moderado', label: 'Moderado' },
  { value: 'grave', label: 'Grave' },
];

interface SupportTicketModalProps {
  open: boolean;
  onClose: () => void;
  companyId?: string;
}

export const SupportTicketModal: React.FC<SupportTicketModalProps> = ({ open, onClose, companyId: propCompanyId }) => {
  const { user, company } = useAuth();
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('otro');
  const [severity, setSeverity] = useState('moderado');
  const [attachments, setAttachments] = useState<File[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const [isForSomeoneElse, setIsForSomeoneElse] = useState(false);
  const [onBehalfUser, setOnBehalfUser] = useState<UserOption | null>(null);
  const [companyUsers, setCompanyUsers] = useState<UserOption[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  const resolvedCompanyId = propCompanyId || company?._id;

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      setSubject('');
      setDescription('');
      setCategory('otro');
      setSeverity('moderado');
      setAttachments([]);
      setError(null);
      setSuccess(false);
      setIsForSomeoneElse(false);
      setOnBehalfUser(null);
    }
  }, [open]);

  // Load company colleagues only once the "for someone else" field is opened
  useEffect(() => {
    if (!open || !isForSomeoneElse || !resolvedCompanyId || companyUsers.length > 0) return;

    const loadUsers = async () => {
      setLoadingUsers(true);
      try {
        const res = await userApi.getSimple({ company: resolvedCompanyId, limit: 200 });
        const users = (res?.data || [])
          .filter((u: any) => u._id !== user?._id)
          .map((u: any) => ({
            _id: u._id,
            name: [u.firstName, u.lastName].filter(Boolean).join(' ') || 'Sin nombre',
            email: u.email,
          }));
        setCompanyUsers(users);
      } catch {
        // Silently fail — dropdown will just be empty
      } finally {
        setLoadingUsers(false);
      }
    };
    loadUsers();
  }, [open, isForSomeoneElse, resolvedCompanyId, companyUsers.length, user?._id]);

  const handleFileAdd = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);
      setAttachments((prev) => [...prev, ...newFiles].slice(0, 5));
    }
  };

  const handleRemoveFile = (index: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!subject.trim()) {
      setError('El asunto es requerido');
      return;
    }
    if (!description.trim()) {
      setError('La descripción es requerida');
      return;
    }
    if (isForSomeoneElse && !onBehalfUser) {
      setError('Seleccione la persona para quien es este ticket');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      await supportTicketApi.create({
        subject: subject.trim(),
        description: description.trim(),
        category,
        severity,
        companyId: resolvedCompanyId,
        onBehalfOfUserId: isForSomeoneElse ? onBehalfUser?._id : undefined,
        attachments: attachments.length > 0 ? attachments : undefined,
      });
      setSuccess(true);
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (err: any) {
      setError(err.message || 'Error al enviar el ticket');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <Box component="span" sx={{ fontWeight: 600 }}>
          Nuevo Ticket de Soporte
        </Box>
        <IconButton onClick={onClose} size="small" aria-label="Cerrar">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {success ? (
          <Alert severity="success" sx={{ my: 2 }}>
            Ticket enviado exitosamente
          </Alert>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1 }}>
            {error && <Alert severity="error">{error}</Alert>}

            {/* Subject */}
            <TextField
              label="Asunto"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              required
              fullWidth
              placeholder="Resumen breve del problema o solicitud"
            />

            {/* On behalf of a colleague */}
            <Box>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isForSomeoneElse}
                    onChange={(e) => {
                      setIsForSomeoneElse(e.target.checked);
                      if (!e.target.checked) setOnBehalfUser(null);
                    }}
                  />
                }
                label="Es para otra persona"
              />
              {isForSomeoneElse && (
                <Autocomplete
                  options={companyUsers}
                  getOptionLabel={(opt) => opt.name}
                  isOptionEqualToValue={(opt, val) => opt._id === val._id}
                  value={onBehalfUser}
                  onChange={(_, val) => setOnBehalfUser(val)}
                  loading={loadingUsers}
                  renderOption={(props, opt) => (
                    <li {...props} key={opt._id}>
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography variant="body2">{opt.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {opt.email}
                        </Typography>
                      </Box>
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Persona"
                      placeholder="Buscar compañero de empresa..."
                      helperText="Solo compañeros de su misma empresa"
                    />
                  )}
                />
              )}
            </Box>

            {/* Category */}
            <FormControl fullWidth>
              <InputLabel>Categoría</InputLabel>
              <Select
                value={category}
                label="Categoría"
                onChange={(e) => setCategory(e.target.value)}
              >
                {CATEGORY_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Severity */}
            <FormControl fullWidth>
              <InputLabel>Severidad</InputLabel>
              <Select
                value={severity}
                label="Severidad"
                onChange={(e) => setSeverity(e.target.value)}
              >
                {SEVERITY_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Description */}
            <TextField
              label="Descripción"
              multiline
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              required
              fullWidth
              placeholder="Describa el problema, queja o sugerencia en detalle..."
            />

            {/* Attachments (images + PDFs) */}
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Archivos adjuntos (opcional, máx. 5 — imágenes o PDF)
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                {attachments.map((file, i) => (
                  <Chip
                    key={i}
                    label={file.name.length > 20 ? file.name.slice(0, 20) + '...' : file.name}
                    size="small"
                    onDelete={() => handleRemoveFile(i)}
                    deleteIcon={<DeleteIcon fontSize="small" />}
                  />
                ))}
                {attachments.length < 5 && (
                  <Button
                    component="label"
                    size="small"
                    variant="outlined"
                    startIcon={<AttachFileIcon />}
                  >
                    Agregar
                    <input
                      type="file"
                      hidden
                      aria-label="Seleccionar archivo adjunto"
                      accept="image/*,application/pdf"
                      multiple
                      onChange={handleFileAdd}
                    />
                  </Button>
                )}
              </Box>
            </Box>
          </Box>
        )}
      </DialogContent>

      {!success && (
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={onClose} color="inherit">
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            disabled={
              submitting ||
              !subject.trim() ||
              !description.trim() ||
              (isForSomeoneElse && !onBehalfUser)
            }
            startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {submitting ? 'Enviando...' : 'Enviar Ticket'}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};
